import {
  collection,
  addDoc,
  doc,
  updateDoc,
  runTransaction,
  serverTimestamp,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { CartItem, ParentOrder } from "../types/index";
import { call, callableMessage, newIdempotencyKey } from "./callables";

/**
 * Helper to remove 'undefined' values from objects.
 * ⚠️ CRITICAL: Do NOT pass serverTimestamp() into this function. 
 * JSON.stringify() destroys Firestore sentinel objects.
 */
const sanitize = (obj: any) => {
  return JSON.parse(JSON.stringify(obj, (key, value) => {
    return value === undefined ? null : value;
  }));
};

// Multi-kitchen USP: never lock cart to one kitchen.
/**
 * Place a new order with delivery fee.
 * Groups cart items by kitchen → 1 parent_orders doc + 1 orders doc per kitchen.
 * Returns { parentOrderId, kitchenOrderIds }.
 */
export type AwaitingChef = { kitchenId: string; orderId: string; kitchenName: string; items: { name: string; cutoff: string }[] };
export type PlaceOrderResult = {
  parentOrderId: string;
  kitchenOrderIds: string[];
  duplicate?: boolean;
  totalAmount?: number;
  awaitingChef?: AwaitingChef[];
  confirmMinutes?: number;
};

export type ServerQuote = {
  items: Array<{ id: string; menuItemId: string; name: string; price: number; quantity: number; kitchenId: string; kitchenName: string; cutoff?: string; pastCutoff?: boolean; clientPrice?: number }>;
  problems: Array<{ index: number; name: string; kitchenId: string; reason: string }>;
  cutoffKitchens: Array<{ kitchenId: string; kitchenName: string; items: { name: string; cutoff: string }[] }>;
  subtotal: number;
  deliveryFee: number;
  platformFee: number;
  discount: number;
  totalAmount: number;
  isCommunity: boolean;
  walletBalance: number | null;
  walletCovers: boolean | null;
  payuShortfall: number | null;
  confirmMinutes: number;
};

const cartPayload = (items: CartItem[]) => items.map((item) => sanitize({
  id: item.id || "",
  name: item.name || "",
  price: item.price || 0,
  quantity: item.quantity || 1,
  kitchenId: item.kitchenId || "",
  kitchenName: item.kitchenName || "",
  customizations: item.customizations || null,
  ...(item.menuItemId ? { menuItemId: String(item.menuItemId) } : {}),
  ...(item.selections && typeof item.selections === "object" ? { selections: item.selections } : {}),
}));

/** Server-side bill for the cart: live-menu prices, fees from config/rates, cut-off status, wallet split. */
export const quoteOrder = async (items: CartItem[], deliveryAddress?: any): Promise<ServerQuote> => {
  try {
    return await call<ServerQuote>("quoteOrder", { items: cartPayload(items), deliveryAddress: deliveryAddress ? sanitize(deliveryAddress) : null });
  } catch (error: any) {
    throw new Error(callableMessage(error, "Couldn't price your cart. Please try again."));
  }
};

export const placeOrder = async (
  userId: string,
  items: CartItem[],
  subtotal: number,
  paymentMethod: "wallet" | "upi" | "upi-qr" | "food",
  paymentStatus?: string,
  deliveryFee: number = 0,
  extra?: {
    kitchenId?: string;
    kitchenName?: string;
    customerName?: string;
    customerPhone?: string;
    platformFee?: number;
    deliveryAddress?: any;
    /** Same key => same order (server dedupes retries / reloads). */
    idempotencyKey?: string;
    /** PayU txnid that funded this order (links txnid/mihpayid onto the orders). */
    payuTxnid?: string;
    /** Total the customer saw (server quote). The server refuses if its own total differs. */
    expectedTotal?: number;
  }
): Promise<PlaceOrderResult> => {
  // Orders + wallet debit are created server-side (placeOrder callable) so the client can't
  // mark orders paid or touch balances. Grouping stays multi-kitchen: 1 parent + 1 order/kitchen.
  const sanitizedItems = items.map((item) => sanitize({
    ...item,
    id: item.id || "",
    name: item.name || "Unnamed Item",
    price: item.price || 0,
    quantity: item.quantity || 1,
    kitchenId: item.kitchenId || "",
    kitchenName: item.kitchenName || "",
    photoUrl: item.photoUrl || "",
    notes: item.notes || null,
    customizations: item.customizations || null,
    ...(item.menuItemId ? { menuItemId: String(item.menuItemId) } : {}),
    ...(item.selections && typeof item.selections === "object" ? { selections: item.selections } : {}),
  }));
  if (sanitizedItems.length === 0) throw new Error("Cart is empty");

  // "upi" without a confirmed QR payment is treated as FOOD (cash) — same as before.
  const method: "wallet" | "upi-qr" | "food" =
    paymentMethod === "wallet"
      ? "wallet"
      : paymentMethod === "upi-qr" || (paymentMethod === "upi" && paymentStatus === "pending_verification")
        ? "upi-qr"
        : "food";

  try {
    const res = await call<PlaceOrderResult>("placeOrder", {
      items: sanitizedItems,
      subtotal,
      deliveryFee: Math.max(0, deliveryFee || 0),
      platformFee: Math.max(0, extra?.platformFee ?? 0),
      paymentMethod: method,
      customerName: extra?.customerName || "",
      customerPhone: extra?.customerPhone || "",
      deliveryAddress: extra?.deliveryAddress ? sanitize(extra.deliveryAddress) : null,
      idempotencyKey: extra?.idempotencyKey || newIdempotencyKey("ord"),
      payuTxnid: extra?.payuTxnid || null,
      expectedTotal: typeof extra?.expectedTotal === "number" ? extra.expectedTotal : null,
    });
    return res;
  } catch (error: any) {
    console.error("Error placing order:", error);
    throw new Error(callableMessage(error, "Order failed. Please try again."));
  }
};

/**
 * Updates order status.
 * ✅ UPDATED: Generates 3-DIGIT OTP (100-999)
 * ✅ UPDATED: Supports 'bypassOtp' to default OTP to "123"
 */
export const updateOrderStatus = async (
  orderId: string,
  newStatus: string,
  metadata: any = {}
) => {
  try {
    await runTransaction(db, async (transaction) => {
      const orderRef = doc(db, "orders", orderId);
      const orderSnap = await transaction.get(orderRef);

      if (!orderSnap.exists()) {
        throw new Error("Order does not exist");
      }

      const orderData = orderSnap.data() as any;
      const cleanMetadata = sanitize(metadata);

      // Start updates object with status and safe metadata
      const updates: any = {
        status: newStatus,
        updatedAt: serverTimestamp(), // Assign directly
        ...cleanMetadata,
      };

      // 1. GENERATE OTP LOGIC
      if (newStatus === "out_for_delivery" && !orderData.deliveryOtp) {
        
        // Check if Chef requested to skip random OTP
        if (metadata.bypassOtp === true) {
            updates.deliveryOtp = "123"; // ✅ Default 3-digit bypass
            updates.otpBypassed = true;
        } else {
            // ✅ Generate random 3-digit OTP (100 - 999)
            updates.deliveryOtp = Math.floor(100 + Math.random() * 900).toString();
        }
      }

      // 2. CASH COLLECTION (food wallet / COD / cash)
      const payRaw = String(orderData.paymentMethod || orderData.paymentMode || "").toLowerCase();
      const isCashCollect =
        payRaw.includes("food") || payRaw.includes("cod") || payRaw.includes("cash");
      if (newStatus === "delivered" && isCashCollect) {
        const agentId = metadata.deliveryAgentId || orderData.deliveryAgentId;
        
        if (agentId) {
          updates.paymentStatus = "paid";
          updates.collectedByAgent = true;
          updates.collectedAt = serverTimestamp(); // Assign directly

          const agentRef = doc(db, "users", agentId);
          const agentSnap = await transaction.get(agentRef);

          if (agentSnap.exists()) {
            const currentCash = agentSnap.data().cashInHand || 0;
            const orderTotal = orderData.totalAmount || 0;
            
            transaction.update(agentRef, {
              cashInHand: currentCash + orderTotal,
            });
          }
        } else {
          console.warn(`No delivery agent assigned for order ${orderId}`);
        }
      }

      transaction.update(orderRef, updates);
    });

    return true;
  } catch (error) {
    console.error("Error updating order status:", error);
    throw error;
  }
};

/**
 * Update payment status (for admin verification)
 */
export const updatePaymentStatus = async (
  orderId: string,
  newPaymentStatus: "paid" | "pending_verification" | "food" | "failed"
) => {
  try {
    const updateData: any = {
      paymentStatus: newPaymentStatus,
      updatedAt: serverTimestamp(),
    };

    if (newPaymentStatus === "paid") {
      updateData.paymentVerifiedAt = serverTimestamp();
    }

    const orderRef = doc(db, "orders", orderId);
    await updateDoc(orderRef, updateData);
    
    return true;
  } catch (error) {
    console.error("Error updating payment status:", error);
    throw error;
  }
};

/**
 * Report an issue with a specific order
 */
export const reportIssueWithOrder = async (
  orderId: string,
  issueType: string,
  description: string
) => {
  try {
    const ticketData = {
      ...sanitize({
        orderId,
        issueType,
        description,
        status: "open",
      }),
      createdAt: serverTimestamp(), // Assign directly
    };

    await addDoc(collection(db, "support_tickets"), ticketData);

    const orderRef = doc(db, "orders", orderId);
    await updateDoc(orderRef, { hasSupportTicket: true });

    return true;
  } catch (error) {
    console.error("Error reporting issue:", error);
    throw error;
  }
};

/**
 * Cancel a (parent) order. Server validates ownership/status and refunds wallet-paid orders
 * to the wallet with a ledger record.
 */
export const cancelOrder = async (
  parentOrderId: string,
  reason?: string
): Promise<{ success: boolean; refunded: number; message: string }> => {
  try {
    return await call("cancelOrder", { parentOrderId, reason: reason || "Cancelled by customer" });
  } catch (error: any) {
    console.error("Error cancelling order:", error);
    throw new Error(callableMessage(error, "Could not cancel order"));
  }
};

/**
 * Get parent order history for a user
 */
export const getParentOrderHistory = async (userId: string): Promise<ParentOrder[]> => {
  try {
    if (!userId) return [];

    // List kitchen-level `orders` (multi-kitchen children + legacy single docs).
    // Match either userId or customerId for web + WhatsApp identity consistency.
    const ordersRef = collection(db, "orders");
    const [byUser, byCustomer] = await Promise.all([
      getDocs(query(ordersRef, where("userId", "==", userId))),
      getDocs(query(ordersRef, where("customerId", "==", userId))),
    ]);

    const merged = new Map<string, any>();
    for (const docSnap of [...byUser.docs, ...byCustomer.docs]) {
      merged.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
    }

    const list = Array.from(merged.values()).sort((a, b) => {
      const aSec = a.createdAt?.seconds ?? a.createdAt?.toMillis?.() ?? 0;
      const bSec = b.createdAt?.seconds ?? b.createdAt?.toMillis?.() ?? 0;
      return bSec - aSec;
    });

    return list.map((data) => {
      return {
        id: data.id,
        userId: data.userId || userId,
        customerId: data.customerId || data.userId || userId,
        customerName: data.customerName || "Unknown",
        customerPhone: data.customerPhone || "",
        items: data.items || [],
        shippingAddress: data.shippingAddress || data.deliveryAddress || "",
        status: data.status || "pending",
        hasComplaint: data.hasComplaint || false,
        feedbackSubmitted: data.feedbackSubmitted || false,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        subtotal: data.subtotal || 0,
        deliveryFee: data.deliveryFee || 0,
        totalAmount: data.totalAmount || (data.subtotal || 0),
        kitchenOrderIds: data.kitchenOrderIds || [],
        kitchenId: data.kitchenId || "",
        kitchenName: data.kitchenName || "",
        parentOrderId: data.parentOrderId || null,
        isSettledToKitchen: data.isSettledToKitchen || false,
        deliveryOtp: data.deliveryOtp || null,
      } as ParentOrder;
    }) as ParentOrder[];
  } catch (error) {
    console.error("Error fetching parent order history:", error);
    throw error;
  }
};

/**
 * Get order summary for display
 */
export const getOrderSummary = async (orderId: string) => {
  try {
    const orderRef = doc(db, "orders", orderId);
    const orderSnap = await getDoc(orderRef);

    if (!orderSnap.exists()) {
      throw new Error("Order not found");
    }

    const data = orderSnap.data();
    
    return {
      id: orderSnap.id,
      subtotal: data.subtotal || 0,
      deliveryFee: data.deliveryFee || 0,
      totalAmount: data.totalAmount || 0,
      items: data.items || [],
      status: data.status,
      paymentMethod: data.paymentMethod,
      paymentStatus: data.paymentStatus,
      createdAt: data.createdAt,
      
      // ✅ ADDED: Expose OTP for order summary
      deliveryOtp: data.deliveryOtp || null,
    };
  } catch (error) {
    console.error("Error fetching order summary:", error);
    throw error;
  }
};